import {
  AfterViewInit,
  Component,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import {
  ListViewComponent,
  listRow,
  AutoDropdownComponent,
  getExtraBurgerMenuOptions,
  MenuOption,
} from '@standard-angular-utilities/shared-components';
import { User, UserRoleType, LogDTO, LogType, ActionType } from 'BaseDataTypes';
import { UserService } from 'BaseServices/user-service';

import { FormsModule } from '@angular/forms';
import { CourseEventLogService } from 'TrainingTrackingServices/course-event-log-service';
import { CourseEvent } from 'TrainingTrackingDataTypes';
import { Department, InternalTitle } from 'BaseDataTypes';
import { MatDialog } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { UserDialogComponent } from '../../Dialogs/user-dialog/user-dialog.component';
import { DepartmentService } from 'TrainingTrackingServices/department-service';
import { InternalTitleService } from 'TrainingTrackingServices/internal-titles-service';
import { LogService } from 'BaseServices/log-service';
import { LogHelperService } from 'BaseServices/log-helper.service';

@Component({
  selector: 'app-user-view',
  imports: [
    ListViewComponent,
    FormsModule,
    MatButtonModule,
    AutoDropdownComponent,
  ],
  templateUrl: './user-view.component.html',
  styleUrl: './user-view.component.css',
})
export class UserViewComponent implements AfterViewInit, OnInit {
  @ViewChild('listView') listView!: ListViewComponent<User>;
  @ViewChild('logListView') logListView!: ListViewComponent<LogDTO>;
  onChangeFunction: () => void = () => {};
  userHeaderList: string[] = [
    'Navn',
    'Email',
    'Intern titel',
    'Afdeling',
    'Nærmeste leder',
  ];
  headerList: string[] = this.userHeaderList;
  logHeaderList: string[];
  isHistory: boolean = false;
  currentUser = this.userService.currentUser;
  selectedUser: number = 0;
  selectedDepartment: number = 0;
  selectedInternalTitle: number = 0;
  selectedManager: number = -1;
  defaultUser: User = {
    id: 0,
    name: '',
    initials: '',
    employeeNumber: 0,
    email: '',
    departmentId: 0,
    internalTitleId: 0,
    nearestManagerId: 0,
    courseRole: UserRoleType.User,
    timelogRole: UserRoleType.Unauthorized,
    assetRole: UserRoleType.Unauthorized,
    equipmentRole: UserRoleType.Unauthorized,
  };

  readonly dialog = inject(MatDialog);

  users: User[] = [];
  departments: Department[] = [];
  internalTitles: InternalTitle[] = [];
  nearestManagers: User[] = [];
  extraBurgerMenuOptions: MenuOption[] = [];
  constructor(
    private courseEventLogService: CourseEventLogService,
    private userService: UserService,
    private departmentService: DepartmentService,
    private internalTitleService: InternalTitleService,
    private logService: LogService,
    private logHelper: LogHelperService
  ) {
    this.logHeaderList = this.logService.getLogHeaders('Bruger');
  }

  ngOnInit() {
    const isAdmin =
      this.userService.getSelectedRole ===
      this.userService.getUserRoleTypes.Admin;
    const customLabel = 'Skift til brugere';

    this.extraBurgerMenuOptions = getExtraBurgerMenuOptions(
      isAdmin,
      this.isHistory,
      customLabel,
      this.switchDataInList.bind(this),
      () => this.extraBurgerMenuOptions[0][0],
      (newLabel: string) => {
        this.extraBurgerMenuOptions[0][0] = newLabel;
      }
    );
  }

  ngViewInit(): void {}

  ngAfterViewInit(): void {
    this.updateListWithUserData();
    this.getDepartments();
    this.getInternalTitles();
    this.getNearestManagers();
  }

  /**
   * get all departments from backend
   */
  getDepartments() {
    this.departmentService.getDepartments().subscribe((result) => {
      this.departments = result;
    });
  }

  /**
   * get all internalTitles from backend
   */
  getInternalTitles() {
    this.internalTitleService.getInternalTitles().subscribe((result) => {
      this.internalTitles = result;
    });
  }

  /**
   * get all nearestManagers from backend
   */
  getNearestManagers() {
    this.userService.getNearestManagers().subscribe((result) => {
      this.nearestManagers = result;
      let noLeaderUser = structuredClone(this.defaultUser);
      noLeaderUser.name = 'Nærmeste leder ikke tilføjet';

      this.nearestManagers.unshift(noLeaderUser);
    });
  }

  /**
   * @param user Filter function given to list-view
   * @returns
   */
  filterFunction = (user: User) => {
    if (this.isHistory) {
      //EventLog mode - got no filters right now
      return true;
    } else {
      //CourseParticipant mode
      if (this.selectedUser != 0 && user.id != this.selectedUser) {
        return false;
      }

      if (
        this.selectedDepartment != 0 &&
        user.departmentId != this.selectedDepartment
      ) {
        return false;
      }

      if (
        this.selectedInternalTitle != 0 &&
        user.internalTitleId != this.selectedInternalTitle
      ) {
        return false;
      }
      if (
        this.selectedManager != -1 &&
        user.nearestManagerId != this.selectedManager
      ) {
        return false;
      }

      return true;
    }
  };

  /**
   * Function which creates a default user, and opens it in the dialog
   */
  createNewUser() {
    this.openUserDialog(structuredClone(this.defaultUser));
  }

  /**
   * Function which opens UserDialogComponent with the supplied user
   * @param user
   */
  openUserDialog(user: User) {
    let dialogref = this.dialog.open(UserDialogComponent, {
      data: { data: [structuredClone(user)] },
    });
    dialogref.afterClosed().subscribe((result) => {
      if (result == true) {
        this.updateListWithUserData();
      }
    });
  }

  /**
   * Helper function for updateListWithEventlogData
   * @param id
   * @returns Name by user id or "Bruger ikke fudet"
   */
  getUserNameFromId(id: number): string {
    return this.users.find((user) => user.id === id)?.name || id.toString();
  }

  /**
   * Function which switches the list between courses and the course event log.
   */
  switchDataInList() {
    if (this.isHistory) {
      this.updateListWithUserData();
      this.isHistory = false;
    } else {
      this.updateListWithLogData();
      this.isHistory = true;
    }
  }

  /**
   * Function to update list with eventlog data
   */
  updateListWithLogData() {
    this.logHelper.updateLogList(
      LogType.User,
      this.getUserNameFromId.bind(this),
      (data) => (this.logListView.listData().listData = data),
      () => this.logListView.update()
    );
  }

  /**
   * Function to updates list with user data
   */
  updateListWithUserData() {
    this.userService.getUsers().subscribe((result) => {
      let tempList: listRow<User>[] = [];
      result.forEach((user) => {
        tempList.push({
          object: user,
          id: user.id,
          data: [
            user.name,
            user.email,
            user.internalTitle!.name,
            user.department!.displayName,
            user.nearestManager
              ? user.nearestManager.name
              : 'Nærmeste leder ikke tilføjet',
          ],
        });
      });
      this.users = result;
      this.headerList = this.userHeaderList;
      this.listView.listData().listData = tempList;
      this.listView.update();
    });
  }

  //triggers listView update only when its avaiable
  get listViewUpdateFn(): () => void {
    return this.listView?.update.bind(this.listView) ?? (() => {});
  }
}
